import React from 'react';
import { useTranslation } from 'react-i18next';
import { DropDown } from '@/components/design-system/molecules/dropdown';
import { PaginatedColocApiConnectedDropDown } from '@/features/shared/feature-paginated-coloc-api-connected-dropdown';

import { usePairTransactionWithRentContext } from '@/features/admin/transactions/components/incoming-transactions-list/hooks/use-pair-transaction-with-rent';
import { House } from '@/types/admin';
import { useGetHousesInfinite } from '@/features/shared/hooks/use-get-houses-infinite';

const PairTransactionHouseSelectionDropDown = () => {
  const { selectedHouse, setSelectedHouse, housesQuery, setHousesQuery } =
    usePairTransactionWithRentContext()!;

  const { t } = useTranslation();

  const housesDataSource = useGetHousesInfinite({
    searchQuery: housesQuery,
  })!;

  // Try to match selectedHouse with items in the list to get the full object
  // If not found, still use selectedHouse (it was explicitly set from room-tenant)
  const matchedHouse = React.useMemo(() => {
    if (!selectedHouse) {
      return null;
    }

    // If we have loaded pages, try to find a match
    if (housesDataSource.fetch.data?.pages) {
      for (const page of housesDataSource.fetch.data.pages) {
        const found = page.data.data.find((house) => house.id === selectedHouse.id);
        if (found) return found;
      }
    }

    // If no match found but selectedHouse exists, use it anyway (was explicitly set)
    return selectedHouse;
  }, [selectedHouse, housesDataSource.fetch.data?.pages]);

  return (
    <>
      <div tabIndex={0} className="sr-only" aria-hidden="true" />
      <PaginatedColocApiConnectedDropDown<House>
        placeholder={
          t(
            'js.features.admin.transactions.transaction_pairing_modal.house_selection_placeholder'
          ) ?? ''
        }
        className="z-50 w-full"
        showLabel
        label={t(
          'js.features.admin.transactions.transaction_pairing_modal.house_selection_placeholder'
        )}
        value={matchedHouse}
        onChange={setSelectedHouse}
        {...(matchedHouse && {
          displayValue: matchedHouse.name,
        })}
        onTextInputChange={(event) => setHousesQuery(event.target.value)}
        dataSource={housesDataSource}
        itemRenderer={(itemProps, ref) => {
          return (
            // @ts-ignore
            <DropDown.DropDownOptionDefaultContent ref={ref}>
              {itemProps.name}
            </DropDown.DropDownOptionDefaultContent>
          );
        }}
      />
    </>
  );
};

export { PairTransactionHouseSelectionDropDown };
